import { isV2CoaLens, isV3CoaLens } from '@opengov/reporting-types';
import { createSelector } from '@opengov/compose-app/reselect';
import { reportLocaleText } from '../../../common/locale';
import {
  selectFormsAndWorkflowsFlag,
  selectMultiHierarchyCoaFinancialReportsFlag,
  selectPerformanceManagementFlag
} from '../../../common/flags';
import { selectNotificationPreferencesTabOptions } from '../../../common/workspaceSidebar/notificationPreferencesTab';
import { selectCanCreateReport, selectReportsHrefReactRouterVersion } from '../../../common/report/selectors';
import { selectEntityLenses } from '../../../lenses/lensSelectors';
import { selectNewReport } from '../../selectors/selectReportCreateState';
import { reportCreateActionCreators } from '../../state';
import { IReportCreateRootProps } from '../ReportCreate';
import { getEntity, getEntitySubdomain } from '@opengov/app-core/entity';
import { NavigateFunction } from 'react-router-dom';
import { reportGalleryPathReactRouterVersion } from '../../../../module/routes/paths';
import { folderPathReactRouterVersion } from '../../../../src/pages/reportsPage/hooks/useFolderBreadcrumbs/useFolderBreadcrumbs';

export const selectShouldShowReportTypeStep = createSelector(
  selectEntityLenses,
  selectNewReport,
  selectMultiHierarchyCoaFinancialReportsFlag,
  (lenses, newReport, isMultiHierarchy) => {
    if (isMultiHierarchy || !newReport.lensId) return false;

    const lens = lenses.find((l) => l.id === newReport.lensId);
    return isV2CoaLens(lens) || isV3CoaLens(lens);
  }
);

export const getOnCancelClicked =
  (entitySubdomain: string) => (navigate: NavigateFunction, folderPath: string | null) => {
    const path = folderPath
      ? folderPathReactRouterVersion(entitySubdomain, folderPath)
      : reportGalleryPathReactRouterVersion(entitySubdomain);
    reportCreateActionCreators.resetReportCreateState();
    navigate(path);
  };

export const selectFeatureFlagsForWorkflowWidgets = createSelector(
  selectFormsAndWorkflowsFlag,
  selectPerformanceManagementFlag,
  (isFormsAndWorkflowsFeatureEnabled, isPerformanceManagementFeatureEnabled) => ({
    isFormsAndWorkflowsFeatureEnabled,
    isPerformanceManagementFeatureEnabled
  })
);

export const selectWorkspaceSidebarOptions = createSelector(
  selectFeatureFlagsForWorkflowWidgets,
  selectNotificationPreferencesTabOptions,
  (featureFlagsForWorkflowWidgets, notificationPreferencesTabOptions) => ({
    ...featureFlagsForWorkflowWidgets,
    notificationPreferencesTabOptions
  })
);

export const selectReportCreateRootProps = createSelector(
  reportLocaleText,
  selectReportsHrefReactRouterVersion,
  selectShouldShowReportTypeStep,
  getEntitySubdomain,
  getEntity,
  selectCanCreateReport,
  selectWorkspaceSidebarOptions,
  (
    localeText,
    reportsHref,
    shouldShowReportTypeStep,
    entitySubdomain,
    entity,
    canCreate,
    workspaceSidebarOptions
  ): IReportCreateRootProps => ({
    reportLocaleText: localeText,
    reportsHref,
    shouldShowReportTypeStep,
    onCancelClicked: getOnCancelClicked(entitySubdomain),
    entity,
    canCreate,
    ...workspaceSidebarOptions
  })
);
